/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/api/fields.js
 *
 * Purpose: React Query hook for loading field configuration grouped by division, reshaped
 *     for portal consumption.
 *
 * Scope: Per-division field configuration used to drive dynamic form fields across order,
 *     sample, test, and other entity forms in the portal.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: `useFieldsByDivision` fetches the raw field configuration list and, via the
 *     query's `select` option, transforms it into a map keyed by division ID. For each
 *     division it picks only the entity types relevant to the portal, strips out
 *     LIMS-only/unused attributes (`hidden`, `required`, `order`, etc.), camelizes the
 *     remaining field objects, and separately records which entities have an "attachments"
 *     dummy field so upload UI can be shown conditionally. The query is cached indefinitely
 *     (`staleTime`/`cacheTime` set to `Infinity`) since field configuration rarely changes
 *     within a session.
 *
 * Exports: useFieldsByDivision
 */

import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { useQuery } from 'react-query';
import { EntityType, QueryKeys } from 'shared/constants/entityTypes';
import { AddlFieldType } from 'shared/constants/fields';
import { Fields } from 'shared/utils/api';
import { isNotNil } from 'shared/utils/functions';
import { camelize } from 'shared/utils/string';

const entityName = QueryKeys.DIVISION;

function getAttachmentEntities(fields) {
  return Object.entries(fields)
    .filter(([_, fields]) =>
      fields.some(
        (field) =>
          field.orderRequestField &&
          field.fieldType === AddlFieldType.DUMMY &&
          field.name === 'attachments',
      ),
    )
    .map(([entity]) => entity);
}

function mapFieldConfigByDivisionId(divisions) {
  const pickList = [
    EntityType.ORDER,
    EntityType.SAMPLE,
    EntityType.TEST,
    EntityType.SOURCE,
    EntityType.PROJECT,
    EntityType.CUSTOMER,
    EntityType.CONTACT,
    EntityType.ASSAY,
    EntityType.PANEL,
    EntityType.INVOICE,
  ];
  const omitList = [
    // These are fields that are relevant only for LIMS,
    // or no longer used in the frontend.
    'hidden',
    'global_hidden',
    'portal_hidden',
    'portal_global_hidden',
    'required',
    'reason_required',
    'order',
    'order_receiving',
    'default_value',
  ];
  return divisions.reduce((acc, div) => {
    const fieldsByEntity = mapValues(pick(div.field_config, pickList), (arr) =>
      camelize(arr.map((c) => omit(c, omitList))),
    );
    return {
      ...acc,
      [div.id]: {
        fields: fieldsByEntity,
        attachmentEntities: getAttachmentEntities(fieldsByEntity),
      },
    };
  }, {});
}

export function useFieldsByDivision() {
  const queryKey = [entityName];
  const queryFn = () => Fields.byDivision().then(({ data: res }) => res?.data);
  const { data, ...queryResult } = useQuery({
    queryKey,
    queryFn,
    select: mapFieldConfigByDivisionId,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return {
    data: isNotNil(data) ? data : {},
    ...queryResult,
  };
}
